import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FaHome,
  FaShieldAlt,
  FaPuzzlePiece,
  FaSync,
  FaFolderOpen,
  FaCertificate,
  FaChartBar,
  FaWallet,
  FaChevronDown,
  FaTimes,
  FaExclamationCircle,
  FaUsers,
  FaCog,
} from 'react-icons/fa';
import './Sidebar.css';

const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';

const menuItems = [
  { label: 'Dashboard', icon: <FaHome />, path: '/dashboard' },
  {
    label: 'Policy Management',
    icon: <FaShieldAlt />,
    children: [
      { label: 'Get Cover', path: '/get-cover' },
      { label: 'Active Policies', path: '/active-policies' },
      { label: 'Cancelled Policies', path: '/suspended-policies' },
      { label: 'Bulk Processing', path: '/bulk-processing' },
    ],
  },
    // { label: 'Pending Proposals', path: '/pending-proposals' },
  { label: 'Extensions', icon: <FaPuzzlePiece />, path: '/extensions' },
  {
    label: 'Renewals Management',
    icon: <FaSync />,
    children: [
      { label: 'Due Renewals', path: '/renewals/due' },
      { label: 'Expired', path: '/renewals/expired' },
    ],
  },
  { label: 'Documents', icon: <FaFolderOpen />, path: '/documents' },
  {
    label: 'Certificates Management',
    icon: <FaCertificate />,
    children: [
      { label: 'Generated Certificates', path: '/generated-certificates' },
      { label: 'Failed Certificates', path: '/failed-certificates' },
      { label: 'Certificate Not Generated', path: '/self-service' },
    ],
  },
  {
    label: 'Reports',
    icon: <FaChartBar />,
    children: [
      { label: 'Production Report', path: '/reports/production' },
      { label: 'Commission Report', path: '/reports/commission' },
    ],
  },
  { label: 'Wallet', icon: <FaWallet />, path: '/wallet' },
  { label: 'Double Insurance', icon: <FaExclamationCircle />, path: '/double-insurance' },
  { label: 'My Agents', icon: <FaUsers />, path: '/my-agents', superAgentOnly: true },
  { label: 'Settings', icon: <FaCog />, path: '/settings' },
];

const Sidebar = () => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [isSuperAgent, setIsSuperAgent] = useState(false);

  const toggleSubMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const closeSidebar = () => {
    document.body.classList.remove('sidebar-open');
  };

  // Auto-expand a group when landing directly on one of its child routes (e.g. via the
  // Dashboard "Book new cover" shortcut), so the highlighted link isn't hidden inside a
  // collapsed submenu.
  useEffect(() => {
    const match = menuItems.find(
      (item) => item.children && item.children.some((child) => location.pathname.startsWith(child.path))
    );
    if (match) setOpenMenus((prev) => ({ ...prev, [match.label]: true }));
  }, [location.pathname]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  // Determine whether this agent should see super-agent-only nav items (e.g. My Agents)
  useEffect(() => {
    const agent = localStorage.getItem('definiteagent');
    if (!agent) return;
    (async () => {
      try {
        const credRes = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const credData = await credRes.json();
        const credentials = btoa(`${credData.Username}:${credData.Password}`);
        const dealerRes = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          headers: { 'Authorization': `Basic ${credentials}` },
        });
        const dealer = await dealerRes.json();
        setIsSuperAgent(dealer.IsSuperAgent === 1);
      } catch (err) { /* default to hidden on failure */ }
    })();
  }, []);

  const visibleMenuItems = menuItems.filter((item) => !item.superAgentOnly || isSuperAgent);

  return (
    <>
      {/* Overlay (mobile only) */}
      <div className="sidebar-overlay" onClick={closeSidebar} />

      <aside className="sidebar">
        {/* Mobile close button */}
        <button className="sidebar-close-btn" onClick={closeSidebar} aria-label="Close menu">
          <FaTimes />
        </button>

        <span className="sidebar-label">Menu</span>
        <ul className="sidebar-nav">
          {visibleMenuItems.map((item) => (
            <li key={item.label} className="sidebar-item">
              {item.children ? (
                <>
                  <button
                    className={`sidebar-link ${item.children.some((child) => location.pathname.startsWith(child.path)) ? 'active' : ''}`}
                    onClick={() => toggleSubMenu(item.label)}
                  >
                    <span className="sidebar-link-icon">{item.icon}</span>
                    <span className="sidebar-link-text">{item.label}</span>
                    <FaChevronDown className={`sidebar-chevron ${openMenus[item.label] ? 'open' : ''}`} />
                  </button>
                  <ul className={`sidebar-submenu ${openMenus[item.label] ? 'open' : ''}`}>
                    {item.children.map((child) => (
                      <li key={child.path}>
                        <NavLink
                          to={child.path}
                          className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                        >
                          <span className="sidebar-link-text">{child.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                >
                  <span className="sidebar-link-icon">{item.icon}</span>
                  <span className="sidebar-link-text">{item.label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </aside>
    </>
  );
};

export default Sidebar;
